/**
 * Testimonials data mapper - transforms API response to UI model
 */

import { getDictValue } from '@/lib/utils/dictMapper';

export interface Testimonial {
  id: number;
  text: string;
  author: string;
  role: string;
  rating: number;
  date: string;
  verified: boolean;
}

export interface TestimonialsNavigation {
  showArrows: boolean;
  showDots: boolean;
  autoPlay: boolean;
  autoPlayInterval: number;
}

export interface TestimonialsLayout {
  itemsPerView: number;
  spacing: number;
  cardStyle: string;
  showQuoteIcon: boolean;
  showRating: boolean;
  showDate: boolean;
}

export interface TestimonialsStyling {
  backgroundColor: string;
  cardBackgroundColor: string;
  textColor: string;
  quoteColor: string;
  authorColor: string;
  roleColor: string;
  borderColor: string;
}

export interface TestimonialsModel {
  title: string;
  subtitle: string;
  badge: string;
  testimonials: Testimonial[];
  navigation: TestimonialsNavigation;
  layout: TestimonialsLayout;
  styling: TestimonialsStyling;
}

export interface TestimonialsApiResponse {
  title: string;
  subtitle: string;
  badge: string;
  testimonials: Testimonial[];
  navigation: TestimonialsNavigation;
  layout: TestimonialsLayout;
  styling: TestimonialsStyling;
}

/**
 * Maps API response to Testimonials UI model
 */
export function mapTestimonialsFromApi(apiData: TestimonialsApiResponse): TestimonialsModel {
  return {
    title: apiData.title,
    subtitle: apiData.subtitle,
    badge: apiData.badge,
    testimonials: apiData.testimonials.map(testimonial => ({
      id: testimonial.id,
      text: testimonial.text,
      author: testimonial.author,
      role: testimonial.role,
      rating: testimonial.rating,
      date: testimonial.date,
      verified: testimonial.verified
    })),
    navigation: {
      showArrows: apiData.navigation.showArrows,
      showDots: apiData.navigation.showDots,
      autoPlay: apiData.navigation.autoPlay,
      autoPlayInterval: apiData.navigation.autoPlayInterval
    },
    layout: {
      itemsPerView: apiData.layout.itemsPerView,
      spacing: apiData.layout.spacing,
      cardStyle: apiData.layout.cardStyle,
      showQuoteIcon: apiData.layout.showQuoteIcon,
      showRating: apiData.layout.showRating,
      showDate: apiData.layout.showDate
    },
    styling: {
      backgroundColor: apiData.styling.backgroundColor,
      cardBackgroundColor: apiData.styling.cardBackgroundColor,
      textColor: apiData.styling.textColor,
      quoteColor: apiData.styling.quoteColor,
      authorColor: apiData.styling.authorColor,
      roleColor: apiData.styling.roleColor,
      borderColor: apiData.styling.borderColor
    }
  };
}

/**
 * Maps API response to Testimonials UI model with dictionary translation
 */
export function mapTestimonialsFromApiWithDict(apiData: TestimonialsApiResponse, dict: any): TestimonialsModel {
  const mapped = mapTestimonialsFromApi(apiData);
  
  // Map dictionary keys to actual values
  return {
    title: getDictValue(dict, mapped.title),
    subtitle: getDictValue(dict, mapped.subtitle),
    badge: mapped.badge,
    testimonials: mapped.testimonials.map(testimonial => ({
      id: testimonial.id,
      text: testimonial.text, // Keep testimonial text as they are (not translated)
      author: testimonial.author,
      role: testimonial.role,
      rating: testimonial.rating,
      date: testimonial.date,
      verified: testimonial.verified
    })),
    navigation: mapped.navigation,
    layout: mapped.layout,
    styling: mapped.styling
  };
}
